// /team - Team Command Center. Lists every staff account as a clickable
// MemberCard. People come from /api/firm/staff; per-member counts come from
// /api/firm/assignments, which stays admin-gated - other roles see the
// people without the numbers, never an invented capacity percent.

import { Link, useNavigate } from "react-router-dom";
import { useFirmStaff } from "../../hooks/useFirmStaff";
import { useFirmWorkload } from "../../hooks/useFirmWorkload";
import type { StaffWorkloadRow } from "../../api/firmWorkload";
import { colorFor, heatColor } from "../../lib/staffVisuals";
import { initialsOf } from "../../api/auth";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { ARROW, MAIL, SEP } from "../chrome/SepList";

function Body({ children }: { children: React.ReactNode }) {
  return (
    <main className="cc-main">
      <div className="cc-body">{children}</div>
    </main>
  );
}

export function TeamCommandCenter() {
  const staffQ = useFirmStaff();
  const workloadQ = useFirmWorkload();
  const navigate = useNavigate();

  if (staffQ.isLoading) {
    return (
      <Body>
        <SkeletonGroup label="Loading team">
          <Skeleton width="30%" height={22} />
          <Skeleton width="100%" height={84} radius={10} />
          <Skeleton width="100%" height={160} radius={10} />
        </SkeletonGroup>
      </Body>
    );
  }

  if (staffQ.isError) {
    return (
      <Body>
        <div className="cc-card" style={{ padding: "10px 18px", color: "var(--warn-600)" }}>
          Could not reach the server - the team page is unavailable.
        </div>
      </Body>
    );
  }

  const staff = staffQ.data ?? [];
  const workload = workloadQ.data?.allowed ? workloadQ.data.workload : null;
  const byId = new Map<string, StaffWorkloadRow>((workload ?? []).map((w) => [w.user.id, w]));
  const totalActive = workload ? workload.reduce((acc, w) => acc + w.activeCount, 0) : null;
  const totalOverdue = workload ? workload.reduce((acc, w) => acc + w.overdueCount, 0) : null;
  const overloaded = workload
    ? workload.filter((w) => w.workloadHeat === "overloaded").length
    : null;

  return (
    <Body>
      <div className="team-cc-head">
        <span className="title">Team</span>
        <span className="count">
          {staff.length} {staff.length === 1 ? "member" : "members"}
        </span>
      </div>

      {workload && (
        <div className="team-hero" role="group" aria-label="Team-wide stats">
          <div className="stat" style={{ ["--pa-accent" as string]: "var(--olive)" }}>
            <div className="l">Members</div>
            <div className="n">{staff.length}</div>
            <div className="sub">staff accounts</div>
          </div>
          <div className="stat" style={{ ["--pa-accent" as string]: "var(--olive)" }}>
            <div className="l">Active matters</div>
            <div className="n">{totalActive}</div>
            <div className="sub">assigned across the team</div>
          </div>
          <div
            className="stat"
            style={{
              ["--pa-accent" as string]: totalOverdue ? "var(--red-600)" : "var(--ok-600)",
            }}
          >
            <div className="l">Overdue deadlines</div>
            <div className="n">{totalOverdue}</div>
            <div className="sub">on assigned matters not yet closed</div>
          </div>
          <div
            className="stat"
            style={{
              ["--pa-accent" as string]: overloaded ? "var(--red-600)" : "var(--ok-600)",
            }}
          >
            <div className="l">Overloaded</div>
            <div className="n">{overloaded}</div>
            <div className="sub">carrying an overloaded caseload</div>
          </div>
        </div>
      )}
      {!workload && !workloadQ.isLoading && (
        <div
          className="cc-card"
          style={{ padding: "10px 18px", marginBottom: 14, fontSize: 12, color: "var(--muted)" }}
        >
          {workloadQ.isError
            ? "Could not reach the server - workload numbers are unavailable."
            : "Workload numbers require an admin sign-in."}
        </div>
      )}

      {staff.length === 0 && (
        <div className="cc-card" style={{ padding: "10px 18px", color: "var(--muted)" }}>
          No staff accounts yet.
        </div>
      )}

      <div className="team-grid">
        {staff.map((s) => {
          const w = byId.get(s.id);
          return (
            // A card holding a mailto must not itself be an anchor - nested
            // <a> is invalid DOM. Whole-card click follows the C6 ledger-row
            // contract: div onClick + one real link inside (View profile).
            <div
              key={s.id}
              className="team-card"
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/team/${s.id}`)}
            >
              <div className="av" style={{ background: colorFor(s.id) }}>
                {initialsOf(s.fullName)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className="name">{s.fullName}</div>
                <div className="role">
                  {s.role}
                  {s.isPlaceholder ? " - not signed in yet" : ""}
                </div>
                {w && (
                  <div className="meta">
                    <span>
                      {w.activeCount} active {w.activeCount === 1 ? "matter" : "matters"}
                      {SEP}
                    </span>
                    {w.overdueCount > 0 && (
                      <span style={{ color: "var(--red-700)" }}>
                        {w.overdueCount} overdue {w.overdueCount === 1 ? "deadline" : "deadlines"}
                        {SEP}
                      </span>
                    )}
                    <span style={{ color: heatColor(w.workloadHeat) }}>{w.workloadHeat}</span>
                  </div>
                )}
                <div className="actions" onClick={(e) => e.stopPropagation()}>
                  {s.email && (
                    <a href={`mailto:${s.email}`}>
                      {MAIL} {s.email}
                    </a>
                  )}
                  <Link to={`/team/${s.id}`} style={{ color: "var(--muted)" }}>
                    View profile {ARROW}
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="cc-detail-footer">
        <span>
          {staff.length} team {staff.length === 1 ? "member" : "members"}
        </span>
        {totalActive !== null && (
          <>
            <span className="sep">{SEP}</span>
            <span>{totalActive} active matters</span>
          </>
        )}
      </div>
    </Body>
  );
}
